/**
 * The colour a WebGL viewer clears to, taken from the theme.
 *
 * Both viewers used to clear to a hardcoded `0.05, 0.06, 0.08`, which meant
 * the one full-bleed surface in the product ignored the appearance preference
 * entirely - and in the Light theme sat as an unexplained slab of near-black
 * inside a white card.
 *
 * **The model's own colour is not touched by any of this.** Vertex colour and
 * the texture atlas are the capture's measurement of the object; tinting them
 * to match a UI theme would make the viewer lie about what was reconstructed.
 * What is themed is the void behind the geometry and the chrome over it.
 *
 * The value is read from the canvas's resolved `background-color` rather than
 * from the custom property, deliberately: `--viewer-bg` is a `color-mix()`
 * expression, and an unregistered custom property computes to its token
 * stream, not to a colour. `background-color` is a real colour property, so
 * the browser has already done the mixing and hands back plain `rgb()`.
 *
 * Read once at setup and again when the theme changes - never per frame,
 * which would force a style recalculation sixty times a second.
 */

/** What the sheet used before this existed; the answer if anything fails. */
const FALLBACK: readonly [number, number, number] = [0.05, 0.06, 0.08];

/**
 * `rgb(10, 13, 18)` / `rgb(10 13 18 / 1)` / `rgba(...)` to three 0..1 floats.
 *
 * Anything else - `transparent`, a colour space this browser reports in some
 * other notation, an empty string from a detached node - returns the fallback
 * rather than a wrong colour or a throw.
 */
export function parseCssRgb(value: string): [number, number, number] {
  const numbers = value.match(/[\d.]+/g);
  if (!numbers || numbers.length < 3) return [...FALLBACK];
  const channels = numbers.slice(0, 3).map((n) => Number(n));
  if (channels.some((n) => !Number.isFinite(n))) return [...FALLBACK];
  // A fully transparent background is `rgba(0, 0, 0, 0)`, which is what an
  // element with no background reports. Clearing to black would be wrong; the
  // fallback is the honest answer.
  if (numbers.length >= 4 && Number(numbers[3]) === 0) return [...FALLBACK];
  return channels.map((n) => Math.min(1, Math.max(0, n / 255))) as [
    number,
    number,
    number,
  ];
}

/** The current clear colour for this canvas, in GL's 0..1 floats. */
export function readViewerBackground(canvas: HTMLElement): [number, number, number] {
  try {
    return parseCssRgb(window.getComputedStyle(canvas).backgroundColor);
  } catch {
    return [...FALLBACK];
  }
}

/**
 * Call `onChange` with the clear colour now, and again whenever the theme
 * that decides it changes.
 *
 * Two sources, because there are two ways the answer can move: an explicit
 * choice writes `data-theme` on `<html>`, and "Sistem" leaves that attribute
 * off and follows the OS. Returns an unsubscribe.
 */
export function observeViewerBackground(
  canvas: HTMLElement,
  onChange: (rgb: [number, number, number]) => void,
): () => void {
  const emit = () => onChange(readViewerBackground(canvas));
  emit();

  const observer = new MutationObserver(emit);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });

  const media = window.matchMedia?.("(prefers-color-scheme: light)");
  media?.addEventListener?.("change", emit);

  return () => {
    observer.disconnect();
    media?.removeEventListener?.("change", emit);
  };
}
